import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Skeleton,
    IconButton,
    Tooltip
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatBRL } from '../../../../utils/formatters';
import moment from 'moment';

const FreteGratisTable = ({ data, loading }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY');
    };

    const formatWeight = (weight) => {
        if (!weight) return '-';
        return `${(weight / 1000).toFixed(2)} kg`;
    };

    if (loading) {
        return (
            <TableContainer sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600, width: '30%' }}>Item MLB</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }}>Account ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }}>Custo do Frete</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }}>Peso Cobrável</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }}>Data de Criação</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '10%', textAlign: 'center' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(5)].map((_, index) => (
                            <TableRow key={index}>
                                <TableCell><Skeleton width="80%" /></TableCell>
                                <TableCell><Skeleton width="60%" /></TableCell>
                                <TableCell><Skeleton width="70%" /></TableCell>
                                <TableCell><Skeleton width="60%" /></TableCell>
                                <TableCell><Skeleton width="85%" /></TableCell>
                                <TableCell><Skeleton width="30%" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    bgcolor: 'white',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}
            >
                <LocalShippingIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    Nenhum dado de frete encontrado
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                    Tente ajustar os filtros de busca
                </Typography>
            </Box>
        );
    }

    const minRows = 15;
    const emptyRows = Math.max(0, minRows - (data?.length || 0));

    return (
        <TableContainer sx={{ 
            bgcolor: 'white', 
            borderRadius: 2, 
            border: '1px solid #e0e0e0', 
            overflow: 'auto',
            overflowX: 'hidden',
            minHeight: '400px',
            height: 'calc(100vh - 340px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            width: 'auto'

        }}>
            <Table stickyHeader sx={{ minHeight: '100%', tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '30%', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocalShippingIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                    Item MLB
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '15%', overflow: 'hidden' }}>
                            <Tooltip title="ID da conta do Mercado Livre" arrow>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', cursor: 'help' }}>
                                    Account ID
                                </Typography>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '15%', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MonetizationOnIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                    Frete
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '15%', overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                Peso
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '15%', overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                Data
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333', width: '10%', textAlign: 'center', overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                Ações
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data?.map((row) => (
                        <TableRow
                            key={row.id}
                            sx={{
                                '&:hover': {
                                    bgcolor: '#f8f9fa'
                                },
                                borderBottom: '1px solid #e9ecef'
                            }}
                        >
                            <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: '#0066cc',
                                        bgcolor: '#e7f3ff',
                                        py: 0.4,
                                        px: 1,
                                        borderRadius: 1.5,
                                        display: 'inline-block',
                                        fontSize: '0.75rem',
                                        maxWidth: '95%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        border: '1px solid #cce7ff'
                                    }}
                                >
                                    {row.item_mlb}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" sx={{ 
                                    color: '#6c757d',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {row.user_id || '-'}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" sx={{ 
                                    fontWeight: 600, 
                                    color: '#198754',
                                    bgcolor: '#d1eedcb8',
                                    py: 0.4,
                                    px: 1,
                                    borderRadius: 1.5,
                                    display: 'inline-block',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'nowrap',
                                    border: '1px solid #badbcc'
                                }}>
                                    {formatBRL(row.list_cost)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" sx={{ 
                                    color: '#495057',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {formatWeight(row.billable_weight)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" sx={{ 
                                    color: '#6c757d',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {formatDate(row.createdAt)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                <Tooltip title="Em desenvolvimento" arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled
                                            sx={{
                                                color: '#ccc',
                                                '&.Mui-disabled': {
                                                    color: '#ccc'
                                                }
                                            }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                    
                    {/* Linhas vazias para ocupar o espaço restante */}
                    {emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
                        <TableRow key={`empty-${index}`} sx={{ height: '53px' }}>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f0f0f0' }}>&nbsp;</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FreteGratisTable;